/**
 * lib/plans.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth untuk data plans fallback, metadata visual,
 * serta formatting helper harga/spesifikasi.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface CatalogPlan {
  slug: string;
  name: string;
  ram_mb: number;
  cpu: number;
  disk_mb: number;
  price_monthly: number;
}

export interface PlanMetadata {
  description: string;
  popular: boolean;
  bandwidth: string;
  backup: string;
  support: string;
  ddos: string;
  panel: string;
  features: string[];
}

export const FALLBACK_PLANS: CatalogPlan[] = [
  { slug: 'lite', name: 'Lite', ram_mb: 1024, cpu: 1, disk_mb: 5120, price_monthly: 20000 },
  { slug: 'starter', name: 'Starter', ram_mb: 2048, cpu: 2, disk_mb: 10240, price_monthly: 35000 },
  { slug: 'standard', name: 'Standard', ram_mb: 4096, cpu: 4, disk_mb: 25600, price_monthly: 65000 },
  { slug: 'plus', name: 'Plus', ram_mb: 6144, cpu: 5, disk_mb: 40960, price_monthly: 95000 },
  { slug: 'pro', name: 'Pro', ram_mb: 8192, cpu: 6, disk_mb: 51200, price_monthly: 120000 }
];

export const PLAN_METADATA: Record<string, PlanMetadata> = {
  lite: {
    description: 'Cocok untuk belajar & server ultra kecil',
    popular: false,
    bandwidth: 'Unlimited',
    backup: '1 slot backup',
    support: 'Support tiket',
    ddos: 'Dasar',
    panel: 'Pterodactyl panel',
    features: [
      'Jakarta node',
      'Setup instan < 60 detik',
      'Pterodactyl panel',
      '1 slot backup',
      'Support tiket',
    ],
  },
  starter: {
    description: 'Ideal untuk server kecil & eksperimen',
    popular: false,
    bandwidth: 'Unlimited',
    backup: '1 slot backup',
    support: 'Support tiket',
    ddos: 'Dasar',
    panel: 'Pterodactyl panel',
    features: [
      'Jakarta node',
      'Setup instan < 60 detik',
      'Pterodactyl panel',
      '1 slot backup',
      'Support tiket',
    ],
  },
  standard: {
    description: 'Paling populer — performa terbaik untuk harga',
    popular: true,
    bandwidth: 'Unlimited',
    backup: '3 slot backup otomatis',
    support: 'Priority support',
    ddos: 'Anti-DDoS penuh',
    panel: 'Pterodactyl panel',
    features: [
      'Jakarta node',
      'Setup instan < 60 detik',
      'Pterodactyl panel',
      '3 slot backup otomatis',
      'Priority support',
      'Referral reward Rp 10.000',
    ],
  },
  plus: {
    description: 'Kapasitas lebih besar untuk server aktif',
    popular: false,
    bandwidth: 'Unlimited',
    backup: '3 slot backup otomatis',
    support: 'Priority support',
    ddos: 'Anti-DDoS penuh',
    panel: 'Pterodactyl panel',
    features: [
      'Jakarta node',
      'Setup instan < 60 detik',
      'Pterodactyl panel',
      '3 slot backup otomatis',
      'Priority support',
      'Referral reward Rp 10.000',
    ],
  },
  pro: {
    description: 'Performa maksimal untuk komunitas besar',
    popular: false,
    bandwidth: 'Unlimited',
    backup: '5 slot backup otomatis',
    support: 'Priority support 24/7',
    ddos: 'Anti-DDoS + TCPShield',
    panel: 'Pterodactyl panel',
    features: [
      'Jakarta node premium',
      'Setup instan < 60 detik',
      'Pterodactyl panel',
      '5 slot backup otomatis',
      'Priority support 24/7',
      'Referral reward Rp 10.000',
      'Anti-DDoS protection',
    ],
  },
};

export function getPlanMetadata(slug: string): PlanMetadata {
  return PLAN_METADATA[slug] ?? {
    description: 'Hosting game server berkinerja tinggi',
    popular: false,
    bandwidth: 'Unlimited',
    backup: '1 slot backup',
    support: 'Support tiket',
    ddos: 'Dasar',
    panel: 'Pterodactyl panel',
    features: [
      'Jakarta node',
      'Setup instan < 60 detik',
      'Pterodactyl panel',
      '1 slot backup',
      'Support tiket',
    ],
  };
}

export function formatPrice(amount: number): string {
  return 'Rp ' + String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export function formatPriceNum(amount: number): string {
  return String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}
